(function () {
  if (customElements.get('image-banner-slider')) {
    return;
  }

  class ImageBannerSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.visualIndex = 0;
      this.realSlideCount = 0;
      this.autoplayTimer = null;
      this.isTransitioning = false;
      this.isPointerDown = false;
      this.isDragging = false;
      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.dragOffset = 0;
      this.preventClick = false;
      this.wheelDelta = 0;

      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.handleDragStart = this.handleDragStart.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('.image-banner-slider__viewport');
      this.track = this.querySelector('.image-banner-slider__track');
      this.originalSlides = Array.from(this.querySelectorAll('.image-banner-slider__slide'));
      this.prevButton = this.querySelector('[data-slider-action="prev"]');
      this.nextButton = this.querySelector('[data-slider-action="next"]');
      this.pagination = this.querySelector('.image-banner-slider__pagination');
      this.realSlideCount = this.originalSlides.length;
      this.isLooping = this.realSlideCount > 1;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.realSlideCount > 1;

      const autoplaySpeed = Number.parseInt(this.dataset.autoplaySpeed, 10);
      this.autoplayDelay = Number.isNaN(autoplaySpeed) ? 5000 : autoplaySpeed;

      if (!this.track || this.realSlideCount === 0) {
        return;
      }

      if (this.isLooping) {
        this.createLoopClones();
      }

      this.slides = Array.from(this.querySelectorAll('.image-banner-slider__slide'));
      this.visualIndex = this.isLooping ? 1 : 0;

      this.setupPagination();
      this.bindEvents();
      this.update(false);

      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.unbindEvents();
      this.stopAutoplay();
    }

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }

      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('click', this.handleClick, true);
      this.track.addEventListener('transitionend', this.handleTransitionEnd);

      if (this.viewport) {
        this.viewport.addEventListener('dragstart', this.handleDragStart);
        this.viewport.addEventListener('pointerdown', this.handlePointerDown);
        this.viewport.addEventListener('pointermove', this.handlePointerMove);
        this.viewport.addEventListener('pointerup', this.handlePointerUp);
        this.viewport.addEventListener('pointercancel', this.handlePointerUp);
        this.viewport.addEventListener('wheel', this.handleWheel, { passive: false });
      }
    }

    unbindEvents() {
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('click', this.handleClick, true);

      if (this.track) {
        this.track.removeEventListener('transitionend', this.handleTransitionEnd);
      }

      if (this.viewport) {
        this.viewport.removeEventListener('dragstart', this.handleDragStart);
        this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
        this.viewport.removeEventListener('pointermove', this.handlePointerMove);
        this.viewport.removeEventListener('pointerup', this.handlePointerUp);
        this.viewport.removeEventListener('pointercancel', this.handlePointerUp);
        this.viewport.removeEventListener('wheel', this.handleWheel);
      }
    }

    setupPagination() {
      if (!this.pagination) {
        return;
      }

      this.pagination.innerHTML = '';
      this.paginationButtons = Array.from({ length: this.realSlideCount }, (_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'image-banner-slider__pagination-button';
        button.setAttribute('aria-label', `Go to slide ${index + 1}`);
        button.addEventListener('click', () => this.goTo(index, true));
        this.pagination.appendChild(button);
        return button;
      });
    }

    createLoopClones() {
      const firstSlide = this.originalSlides[0];
      const lastSlide = this.originalSlides[this.originalSlides.length - 1];

      if (!firstSlide || !lastSlide) {
        return;
      }

      const firstClone = firstSlide.cloneNode(true);
      const lastClone = lastSlide.cloneNode(true);

      this.prepareClone(firstClone);
      this.prepareClone(lastClone);

      this.track.insertBefore(lastClone, firstSlide);
      this.track.appendChild(firstClone);
    }

    prepareClone(slide) {
      slide.classList.add('image-banner-slider__slide--clone');
      slide.setAttribute('aria-hidden', 'true');

      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    }

    handlePrevClick() {
      this.prevSlide(true);
    }

    handleNextClick() {
      this.nextSlide(true);
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePrevClick();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNextClick();
      }
    }

    handleMouseEnter() {
      this.stopAutoplay();
    }

    handleMouseLeave() {
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    handleFocusIn() {
      this.stopAutoplay();
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget) && this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    handleDragStart(event) {
      event.preventDefault();
    }

    handleWheel(event) {
      if (this.realSlideCount < 2 || this.isTransitioning) {
        return;
      }

      const primaryDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;

      if (primaryDelta === 0) {
        return;
      }

      event.preventDefault();
      this.stopAutoplay();
      this.wheelDelta += primaryDelta;

      if (Math.abs(this.wheelDelta) < 40) {
        return;
      }

      const direction = this.wheelDelta > 0 ? 1 : -1;
      this.wheelDelta = 0;

      if (direction > 0) {
        this.nextSlide(true);
      } else {
        this.prevSlide(true);
      }
    }

    handlePointerDown(event) {
      if (this.realSlideCount < 2) {
        return;
      }

      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      this.isPointerDown = true;
      this.isDragging = false;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.dragOffset = 0;
      this.stopAutoplay();
    }

    handlePointerMove(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) {
        return;
      }

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (!this.isDragging) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) {
          return;
        }

        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.resetPointerState();

          if (this.autoplayEnabled) {
            this.startAutoplay();
          }

          return;
        }

        this.isDragging = true;

        if (this.viewport) {
          this.viewport.setPointerCapture(event.pointerId);
        }

        this.classList.add('is-dragging');
        this.isTransitioning = false;
      }

      event.preventDefault();
      this.dragOffset = deltaX;
      this.track.style.transform = `translate3d(${this.getBaseOffset() + this.dragOffset}px, 0, 0)`;
    }

    handlePointerUp(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) {
        return;
      }

      this.finishDragInteraction();
    }

    finishDragInteraction() {
      const wasDragging = this.isDragging;
      const shouldChangeSlide = wasDragging && Math.abs(this.dragOffset) > Math.max(this.offsetWidth * 0.15, 50);
      const dragDirection = this.dragOffset > 0 ? -1 : 1;

      this.resetPointerState();

      if (shouldChangeSlide) {
        this.preventClick = true;
        if (dragDirection > 0) {
          this.nextSlide(true);
        } else {
          this.prevSlide(true);
        }
        return;
      }

      this.update();

      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    handleClick(event) {
      if (!this.preventClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.preventClick = false;
    }

    handleTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') {
        return;
      }

      if (this.isLooping) {
        if (this.visualIndex === 0) {
          this.visualIndex = this.realSlideCount;
          this.update(false);
        } else if (this.visualIndex === this.realSlideCount + 1) {
          this.visualIndex = 1;
          this.update(false);
        }
      }

      this.isTransitioning = false;
      this.wheelDelta = 0;
    }

    goTo(index, shouldRestartAutoplay = false) {
      if (this.realSlideCount < 2 || this.isTransitioning) {
        return;
      }

      const nextIndex = (index + this.realSlideCount) % this.realSlideCount;

      if (nextIndex === this.currentIndex) {
        return;
      }

      if (this.isLooping && this.currentIndex === this.realSlideCount - 1 && nextIndex === 0) {
        this.nextSlide(shouldRestartAutoplay);
        return;
      }

      if (this.isLooping && this.currentIndex === 0 && nextIndex === this.realSlideCount - 1) {
        this.prevSlide(shouldRestartAutoplay);
        return;
      }

      this.isTransitioning = true;
      this.currentIndex = nextIndex;
      this.visualIndex = this.isLooping ? nextIndex + 1 : nextIndex;
      this.update();

      if (shouldRestartAutoplay && this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    prevSlide(shouldRestartAutoplay = false) {
      if (this.realSlideCount < 2 || this.isTransitioning) {
        return;
      }

      this.isTransitioning = true;
      this.currentIndex = (this.currentIndex - 1 + this.realSlideCount) % this.realSlideCount;
      this.visualIndex -= 1;
      this.update();

      if (shouldRestartAutoplay && this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    nextSlide(shouldRestartAutoplay = false) {
      if (this.realSlideCount < 2 || this.isTransitioning) {
        return;
      }

      this.isTransitioning = true;
      this.currentIndex = (this.currentIndex + 1) % this.realSlideCount;
      this.visualIndex += 1;
      this.update();

      if (shouldRestartAutoplay && this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    update(animate = true) {
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translate3d(-${this.visualIndex * 100}%, 0, 0)`;

      if (!animate) {
        this.track.offsetHeight;
        this.track.style.transition = '';
      }

      if (!this.paginationButtons) {
        return;
      }

      this.paginationButtons.forEach((button, index) => {
        button.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled) {
        return;
      }

      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.currentIndex + 1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) {
        return;
      }

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    getBaseOffset() {
      return -this.visualIndex * this.viewport.offsetWidth;
    }

    resetPointerState() {
      if (this.viewport && this.pointerId !== null && this.viewport.hasPointerCapture(this.pointerId)) {
        this.viewport.releasePointerCapture(this.pointerId);
      }

      this.isPointerDown = false;
      this.isDragging = false;
      this.pointerId = null;
      this.dragOffset = 0;
      this.classList.remove('is-dragging');
    }
  }

  customElements.define('image-banner-slider', ImageBannerSlider);
})();
